import React from 'react';
import styled from 'styled-components';
import useLocale from '../../hooks/useLocale';

const CATEGORIES = [
  { key: 'both_same_date', accent: '#27AE60' },
  { key: 'different_date', accent: '#F39C12' },
  { key: 'drik_only',      accent: '#2196F3' },
  { key: 'vakyam_only',    accent: '#9C27B0' },
];

const Section = styled.section`
  margin: ${p => p.theme.spacing[4]}px 0;
`;

const Group = styled.div`
  margin-bottom: ${p => p.theme.spacing[3]}px;
`;

const GroupHeader = styled.h4`
  font-size: ${p => p.theme.typography.sizes.md};
  font-weight: ${p => p.theme.typography.weights.semibold};
  color: ${p => p.accent};
  margin: 0 0 ${p => p.theme.spacing[2]}px 0;
  padding-left: ${p => p.theme.spacing[2]}px;
  border-left: 3px solid ${p => p.accent};
`;

const RowContainer = styled.div`
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radius.sm}px;
  margin-bottom: ${p => p.theme.spacing[1]}px;
`;

const FestivalName = styled.div`
  font-size: ${p => p.theme.typography.sizes.md};
  font-weight: ${p => p.theme.typography.weights.semibold};
  color: ${p => p.theme.colors.text.primary};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const FestivalSubname = styled.div`
  font-size: ${p => p.theme.typography.sizes.xs};
  color: ${p => p.theme.colors.text.muted};
  margin-top: 2px;
`;

const DateSplit = styled.div`
  margin-top: ${p => p.theme.spacing[1]}px;
  font-size: ${p => p.theme.typography.sizes.sm};
  font-family: ${p => p.theme.typography.monoFamily};
  color: ${p => p.theme.colors.text.secondary};

  > span { margin-right: ${p => p.theme.spacing[3]}px; }
`;

const SingleDate = styled.div`
  margin-top: ${p => p.theme.spacing[1]}px;
  font-size: ${p => p.theme.typography.sizes.sm};
  font-family: ${p => p.theme.typography.monoFamily};
  color: ${p => p.theme.colors.text.secondary};
`;

function groupByCategory(diffs) {
  const groups = { both_same_date: [], different_date: [], drik_only: [], vakyam_only: [] };
  (diffs || []).forEach(d => {
    if (groups[d.category]) groups[d.category].push(d);
  });
  return groups;
}

function FestivalRow({ diff, lang }) {
  const primary = lang === 'ta' ? (diff.name_ta || diff.name) : diff.name;
  const secondary = lang === 'ta' ? diff.name : diff.name_ta;

  return (
    <RowContainer>
      <FestivalName>{primary}</FestivalName>
      {secondary && secondary !== primary && <FestivalSubname>{secondary}</FestivalSubname>}
      {diff.category === 'different_date' && (
        <DateSplit>
          <span>Drik: {diff.drik_date || '\u2014'}</span>
          <span>Vakyam: {diff.vakyam_date || '\u2014'}</span>
        </DateSplit>
      )}
      {(diff.category === 'drik_only' || diff.category === 'both_same_date') && diff.drik_date && (
        <SingleDate>{diff.drik_date}</SingleDate>
      )}
      {diff.category === 'vakyam_only' && diff.vakyam_date && (
        <SingleDate>{diff.vakyam_date}</SingleDate>
      )}
    </RowContainer>
  );
}

const FestivalDiffsSection = ({ diffs, lang = 'en' }) => {
  const t = useLocale(lang, 'comparison');

  if (!diffs || diffs.length === 0) return null;

  const groups = groupByCategory(diffs);

  return (
    <Section>
      {CATEGORIES.map(cat => {
        const items = groups[cat.key];
        if (items.length === 0) return null;
        const label = t.festival?.[cat.key] || cat.key;
        return (
          <Group key={cat.key}>
            <GroupHeader accent={cat.accent}>{label}</GroupHeader>
            {items.map((d, i) => (
              <FestivalRow key={`${d.name}-${i}`} diff={d} lang={lang} />
            ))}
          </Group>
        );
      })}
    </Section>
  );
};

export default FestivalDiffsSection;
